import { css } from '@emotion/react';
import { useState, useEffect } from 'react';

// 아이콘 템플릿 데이터 (검증된 Animated Fluent Emojis URLs)
const iconTemplates = [
  {
    id: 1,
    iconUrl:
      'https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Animals/Spiral%20Shell.png',
    title: '마법의 소라고둥에게 질문 중'
  },
  {
    id: 2,
    iconUrl:
      'https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Activities/Crystal%20Ball.png',
    title: '운명의 스토리 찾는 중'
  },
  {
    id: 3,
    iconUrl:
      'https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Travel%20and%20places/Compass.png',
    title: '스토리 방향성을 찾는 중'
  },
  {
    id: 4,
    iconUrl:
      'https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/People/Woman%20Detective.png',
    title: '스토리 단서 수집 중'
  },
  {
    id: 5,
    iconUrl:
      'https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Travel%20and%20places/Wind%20Face.png',
    title: '스토리에 생명을 불어넣는 중'
  },
  {
    id: 6,
    iconUrl:
      'https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Smilies/Sparkling%20Heart.png',
    title: '스토리에 심폐소생술 중'
  },
  {
    id: 7,
    iconUrl:
      'https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Hand%20gestures/Writing%20Hand.png',
    title: '스토리 만드는 중'
  },
  {
    id: 8,
    iconUrl:
      'https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Travel%20and%20places/Fountain.png',
    title: '아이디어가 샘솟는 중'
  },
  {
    id: 9,
    iconUrl:
      'https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Travel%20and%20places/Roller%20Coaster.png',
    title: '이야기가 스릴 넘치는 중'
  },
  {
    id: 10,
    iconUrl:
      'https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Hand%20gestures/Mechanical%20Arm.png',
    title: 'AI 파워 노동 중'
  }
];

// 배열을 랜덤하게 섞는 함수
const shuffleArray = (array: typeof iconTemplates) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

function LoadingTemplate() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [shuffledIcons, setShuffledIcons] = useState<typeof iconTemplates>([]);
  const [preloadedImages, setPreloadedImages] = useState<Set<string>>(new Set());

  // 이미지 프리로드 함수
  const preloadImage = (url: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        setPreloadedImages(prev => new Set([...prev, url]));
        resolve();
      };
      img.onerror = reject;
      img.src = url;
    });
  };

  // 컴포넌트 마운트 시 모든 이미지 프리로드 및 아이콘 순서 랜덤화
  useEffect(() => {
    const shuffled = shuffleArray(iconTemplates);
    setShuffledIcons(shuffled);

    // 모든 아이콘 이미지를 미리 로드
    const preloadPromises = iconTemplates.map(template =>
      preloadImage(template.iconUrl).catch(() => {
        // 개별 이미지 로드 실패해도 계속 진행
        console.warn(`Failed to preload image: ${template.iconUrl}`);
      })
    );

    Promise.allSettled(preloadPromises).then(() => {
      console.log('All images preload completed');
    });
  }, []);

  // 5초마다 다음 아이콘으로 변경 (고정된 순서로 무한 루프)
  useEffect(() => {
    if (shuffledIcons.length === 0) {
      return undefined;
    }

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        // 단순히 다음 인덱스로 이동, 마지막에 도달하면 처음부터 반복
        return (prevIndex + 1) % shuffledIcons.length;
      });
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [shuffledIcons]);

  const currentTemplate = shuffledIcons[currentIndex];

  if (!currentTemplate) {
    return null; // 데이터 로딩 중
  }

  return (
    <div css={Container}>
      <div css={ContentContainer}>
        <div css={IconWrap}>
          <img
            src={currentTemplate.iconUrl}
            alt={currentTemplate.title}
            css={IconStyle}
            data-loaded={preloadedImages.has(currentTemplate.iconUrl)}
          />
        </div>
        <div css={Title}>{currentTemplate.title}</div>
        <div css={Description}>
          현재 페이지를 벗어나면 생성 중인 스토리가 모두 사라지니, 잠시만 기다려주세요.
        </div>
      </div>
    </div>
  );
}

export default LoadingTemplate;

const Container = css`
  width: 100%;
  height: calc(100vh - 58px);
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f4f4f4;
`;

const ContentContainer = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 650px;
  padding: 0 24px;
`;

const IconWrap = css`
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
`;

const IconStyle = css`
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: opacity 0.3s ease-in-out;
  
  &[data-loaded="true"] {
    opacity: 1;
  }
  
  &[data-loaded="false"] {
    opacity: 0.5;
  }
`;

const Title = css`
  color: #0e1017;
  font-family: 'Pretendard', sans-serif;
  font-weight: 700;
  font-size: 28px;
  line-height: 32px;
  text-align: center;
  margin-bottom: 16px;
`;

const Description = css`
  color: #797d88;
  font-family: 'Pretendard', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
`;
